// Optimized URL encoding for sharing run plans
// Implements 5 key optimizations:
// 1. Short field names
// 2. Compact date format (ISO)
// 3. Skip default values
// 4. Array-based encoding
// 5. Group by week for better compression

// Type mappings (single char)
const TYPE_MAP = {
  easy: 'e',
  recovery: 'r',
  long: 'l',
  workout: 'w',
  race: 'x',
  cross: 'c',
  rest: 'z'
};

const TYPE_MAP_REV = Object.fromEntries(
  Object.entries(TYPE_MAP).map(([k, v]) => [v, k])
);

// Status mappings (single char)
const STATUS_MAP = {
  planned: 'p',
  completed: 'c',
  adjusted: 'a',
  rest: 'r'
};

const STATUS_MAP_REV = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([k, v]) => [v, k])
);

/**
 * Encode a single run into compact array format
 * Array structure: [distance, type?, status?, notes?, raceName?, actualDistance?]
 * Trailing defaults are omitted
 */
const encodeRun = (run) => {
  const arr = [run.distance || 0];

  // Add type if not default (easy)
  const hasType = run.type && run.type !== 'easy';
  if (hasType) {
    arr.push(TYPE_MAP[run.type] || 'e');
  }

  // Add status if not default (planned) OR if we added type
  const hasStatus = run.status && run.status !== 'planned';
  if (hasStatus || hasType) {
    arr.push(STATUS_MAP[run.status] || 'p');
  }

  // Add notes if present
  if (run.notes) {
    // Pad with nulls if needed
    while (arr.length < 3) arr.push(null);
    arr.push(run.notes);
  }

  // Add race name if present
  if (run.raceName) {
    while (arr.length < 4) arr.push(null);
    arr.push(run.raceName);
  }

  // Add actual distance if adjusted
  if (run.status === 'adjusted' && run.actualDistance !== undefined) {
    while (arr.length < 5) arr.push(null);
    arr.push(run.actualDistance);
  }

  // Remove trailing nulls
  while (arr.length > 0 && arr[arr.length - 1] === null) {
    arr.pop();
  }

  return arr;
};

/**
 * Decode a run array back to full object
 */
const decodeRun = (arr) => {
  if (!Array.isArray(arr)) return null;

  const run = {
    distance: arr[0] || 0,
    type: arr[1] ? TYPE_MAP_REV[arr[1]] || 'easy' : 'easy',
    status: arr[2] ? STATUS_MAP_REV[arr[2]] || 'planned' : 'planned',
    notes: arr[3] || '',
  };

  // Add race name if present
  if (arr[4]) {
    run.raceName = arr[4];
  }

  // Add actual distance if present (for adjusted runs)
  if (arr[5] !== undefined) {
    run.actualDistance = arr[5];
  }

  return run;
};

/**
 * Convert date string to ISO format (YYYY-MM-DD)
 */
const dateToISO = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

/**
 * Encode full plan data into compact format
 */
export const encodePlanData = (planData) => {
  if (!planData || !planData.plan || !planData.runs) {
    throw new Error('Invalid plan data');
  }

  // Compact plan info
  const encoded = {
    p: {
      i: planData.plan.id,
      n: planData.plan.name,
      w: planData.plan.weeks ? planData.plan.weeks.length : 0,
      d: planData.plan.diff
    },
    r: {}
  };

  // Encode runs with ISO dates and compact arrays
  Object.entries(planData.runs).forEach(([dateString, run]) => {
    const isoDate = dateToISO(dateString);
    encoded.r[isoDate] = encodeRun(run);
  });

  return encoded;
};

/**
 * Decode compact format back to full plan data
 */
export const decodePlanData = (encoded) => {
  if (!encoded || !encoded.p || !encoded.r) {
    throw new Error('Invalid encoded data');
  }

  // Reconstruct plan
  const plan = {
    id: encoded.p.i,
    name: encoded.p.n,
    weeks: Array(encoded.p.w || 0).fill(0),
    diff: encoded.p.d
  };

  // Decode runs
  const runs = {};
  Object.entries(encoded.r).forEach(([isoDate, runArray]) => {
    // Convert ISO date back to Date().toDateString() format
    const date = new Date(isoDate);
    const dateString = date.toDateString();
    runs[dateString] = decodeRun(runArray);
  });

  return {
    plan,
    runs
  };
};
